import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { bulkAssignContacts } from '../../api/contacts';
import { useOrganizationStore } from '../../stores/useOrganizationStore';

interface BulkAssignParams {
  name: string;
  phoneNumber: string;
  email: string;
  tag?: string;
  reAssign?: boolean;
}

interface BulkAssignResponse {
  success: boolean;
  message: string;
  updated: number;
  skipped: number;
}

interface UseBulkAssignContactsResult {
  assignContacts: (params: BulkAssignParams) => Promise<BulkAssignResponse>;
  isLoading: boolean;
  error: Error | null;
  isSuccess: boolean;
  reset: () => void;
}

/**
 * Hook for bulk assigning contacts to a team member
 * @returns Object with assignContacts function, loading state, error state, and success state
 */
export const useBulkAssignContacts = (): UseBulkAssignContactsResult => {
  const { organization } = useOrganizationStore();
  const [error, setError] = useState<Error | null>(null);

  const mutation = useMutation({
    mutationFn: async (params: BulkAssignParams) => {
      if (!organization?.id) {
        throw new Error('Organization ID is required');
      }

      try {
        // Call the API and return the full response
        const result = await bulkAssignContacts(
          organization.id,
          params.name,
          params.phoneNumber,
          params.email,
          params.tag,
          params.reAssign ?? false
        );
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to bulk assign contacts');
        setError(error);
        throw error;
      }
    },
  });

  const assignContacts = async (params: BulkAssignParams): Promise<BulkAssignResponse> => {
    setError(null);
    // Return the full response data from the API
    return await mutation.mutateAsync(params);
  };

  return {
    assignContacts,
    isLoading: mutation.isPending,
    error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};

export default useBulkAssignContacts;
